import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { SimpleDialog } from "@/components/ui/simple-dialog";
import { UserCheck, X, ChevronDown, Loader2 } from "lucide-react";

/**
 * ProxyClaimDialog
 *
 * Opens when a user triggers a proxy claim (MMB on desktop, 1-second long
 * press on touch devices). Lets the operator select a target user from the
 * whitelist and submit the claim on their behalf.
 *
 * Props:
 *   open          – boolean controlling visibility
 *   onOpenChange  – (bool) => void
 *   card          – the card being claimed
 *   currentUser   – the logged-in user object { name, email, ... }
 *   userNickname  – the current user's nickname (preferred display name)
 *   onConfirm     – async (card, targetNickname) => void  called on submit
 *   mode          – current user mode string ("group" | "private")
 */
export default function ProxyClaimDialog({
  open,
  onOpenChange,
  card,
  currentUser,
  userNickname,
  onConfirm,
  mode,
}) {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Fetch the whitelist nicknames whenever the dialog opens
  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    setError(null);
    try {
      const { data } = await api.get("/users/whitelist");
      // Backend returns [{id, email, nickname}] — extract nicknames for the dropdown,
      // excluding the current user so they cannot claim on behalf of themselves.
      const nicknames = Array.isArray(data)
        ? data
            .filter((u) => u.email !== currentUser?.email)
            .map((u) => u.nickname)
            .filter(Boolean)
        : [];
      setUsers(nicknames);
      // Pre-select the first nickname in the list (or keep empty if none)
      setSelectedUser(nicknames.length > 0 ? nicknames[0] : "");
    } catch (e) {
      setError("Failed to load user list.");
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [currentUser?.email]);

  useEffect(() => {
    if (open) {
      fetchUsers();
    } else {
      // Reset state when closed
      setSelectedUser("");
      setError(null);
      setSubmitting(false);
    }
  }, [open, fetchUsers]);

  const handleSubmit = async () => {
    if (!selectedUser) {
      setError("Please select a user to claim for.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(card, selectedUser);
      onOpenChange(false);
    } catch (e) {
      setError(
        e?.response?.data?.detail ?? "Failed to submit proxy claim. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !submitting && selectedUser) {
      handleSubmit();
    }
    if (e.key === "Escape") {
      onOpenChange(false);
    }
  };

  // Proxy claims are only meaningful in group mode — in private mode there
  // are no other users to claim on behalf of.
  const isGroupMode = mode === "group" || mode == null;

  const accentBorder = mode === "private" ? "border-emerald-500/30" : "border-orange-500/30";
  const accentHeaderBorder = mode === "private" ? "border-emerald-500/15" : "border-orange-500/15";
  const accentIconBorder = mode === "private" ? "border-emerald-500/40" : "border-orange-500/40";
  const accentIconBg = mode === "private" ? "bg-emerald-500/5" : "bg-orange-500/5";
  const accentIcon = mode === "private" ? "text-emerald-400" : "text-orange-400";
  const accentLabel = mode === "private" ? "text-emerald-400" : "text-orange-400";
  const accentCloseHover = mode === "private" ? "hover:border-emerald-500 hover:text-emerald-400" : "hover:border-orange-500 hover:text-orange-400";
  const accentSubmitBtn = mode === "private"
    ? "border-emerald-500 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500 hover:text-black"
    : "border-orange-500 bg-orange-500/10 text-orange-300 hover:bg-orange-500 hover:text-black";
  const accentSelectFocus = mode === "private" ? "focus:border-emerald-500" : "focus:border-orange-500";

  return (
    <SimpleDialog open={open} onOpenChange={onOpenChange} className="max-w-md">
      <div
        className={`bg-[#0B0E14]/95 backdrop-blur-xl border ${accentBorder} rounded-none text-white w-full`}
        data-testid="proxy-claim-dialog"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b ${accentHeaderBorder}`}>
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 border ${accentIconBorder} flex items-center justify-center ${accentIconBg}`}>
              <UserCheck className={`w-3.5 h-3.5 ${accentIcon}`} />
            </div>
            <div>
              <div className={`text-[10px] tracking-[0.3em] uppercase ${accentLabel} mb-0.5`}>
                Proxy Claim
              </div>
              <div className="font-display font-bold text-base tracking-tight text-slate-200">
                Claim on Behalf Of
              </div>
            </div>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            aria-label="Close"
            className={`flex items-center justify-center w-7 h-7 border border-slate-700 text-slate-400 ${accentCloseHover} transition-colors`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Private-mode guard — proxy claims require group mode */}
        {!isGroupMode ? (
          <div className="px-4 py-6 flex flex-col items-center gap-3 text-center">
            <div className="w-10 h-10 border border-orange-500/40 flex items-center justify-center bg-orange-500/5">
              <UserCheck className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-200 mb-1">
                Group Mode Required
              </div>
              <p className="text-xs text-slate-500 tracking-wide leading-relaxed">
                Proxy claims are only available in Group mode. Switch to Group
                mode to claim cards on behalf of other users.
              </p>
            </div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="mt-2 px-4 py-2 border border-slate-700 text-slate-300 hover:border-slate-500 text-xs tracking-widest uppercase transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Body */}
            <div className="px-4 py-4">
              {/* Card info */}
              <div className="mb-4 px-3 py-2.5 border border-slate-800 bg-[#121822]/60">
                <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-0.5">
                  Card
                </div>
                <div className="text-slate-200 text-sm font-medium">{card?.name ?? "—"}</div>
                {card?.source && (
                  <div className="text-slate-500 text-[11px] mt-0.5">{card.source}</div>
                )}
              </div>

              {/* Claimer info */}
              <div className="mb-4 px-3 py-2.5 border border-slate-800 bg-[#121822]/60">
                <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-0.5">
                  Claimed by (you)
                </div>
                <div className="text-slate-300 text-sm font-mono">
                  {userNickname ?? currentUser?.email ?? "—"}
                </div>
              </div>

              {/* Target user selector */}
              <div className="mb-4">
                <label className="block text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-1.5">
                  Claim for
                </label>
                {loadingUsers ? (
                  <div className="flex items-center gap-2 px-3 py-2.5 border border-slate-700 text-slate-500 text-xs">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Loading users…
                  </div>
                ) : users.length === 0 ? (
                  <div className="px-3 py-2.5 border border-slate-700 text-slate-500 text-xs">
                    No other users available.
                  </div>
                ) : (
                  <div className="relative">
                    <select
                      value={selectedUser}
                      onChange={(e) => setSelectedUser(e.target.value)}
                      data-testid="proxy-user-select"
                      className={`w-full appearance-none px-3 py-2.5 bg-[#0B0E14] border border-slate-700 text-white text-sm font-mono focus:outline-none ${accentSelectFocus} transition-colors pr-8`}
                    >
                      {users.map((nickname) => (
                        <option key={nickname} value={nickname}>
                          {nickname}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
                  </div>
                )}
              </div>

              {/* Hint text */}
              <p className="text-[10px] text-slate-600 tracking-wide mb-4">
                The card timer will reset as normal. This claim will be recorded
                under the selected user's name in the Card Holders log.
              </p>

              {/* Error */}
              {error && (
                <div
                  className="px-3 py-2 border border-orange-500/30 bg-orange-500/10 text-orange-400 text-xs tracking-wide mb-4"
                  data-testid="proxy-claim-error"
                >
                  {error}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 px-4 pb-4">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
                data-testid="proxy-claim-cancel"
                className="px-4 py-2 border border-slate-700 text-slate-300 hover:border-slate-500 text-xs tracking-widest uppercase disabled:opacity-40 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !selectedUser || loadingUsers}
                data-testid="proxy-claim-submit"
                className={`flex items-center gap-1.5 px-5 py-2 border ${accentSubmitBtn} transition-colors text-xs tracking-widest uppercase disabled:opacity-40`}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Claiming…
                  </>
                ) : (
                  <>
                    <UserCheck className="w-3.5 h-3.5" />
                    Confirm Proxy Claim
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </SimpleDialog>
  );
}
